import { useState } from 'react';
import {
  ArrowRight,
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  Eye,
  Home,
  Landmark,
  Receipt,
  Search,
  TrendingUp,
  Wallet,
  X,
} from 'lucide-react';
import type { IncomeComparison, IncomeStream } from '../hooks/useChildSupport';
import { Button, Card, Tag, type TagTone } from './ui';
import { money } from '../lib/money';

interface Props {
  displayName: string;
  streams: IncomeStream[];
  comparison: IncomeComparison | null;
  isMaterial: boolean;
  documentedSource?: string;
  onAccept: (stream: IncomeStream) => void;
  onDismiss: (stream: IncomeStream) => void;
  onViewSource: (v: { statementId: number; pageNumber: number; sourceFile: string }) => void;
  evidenceFor: (stream: IncomeStream) => EvidenceTxn[];
  busyKey?: string | null;
}

export interface EvidenceTxn {
  id: number;
  date: string | null;
  description: string | null;
  amount: number | null;
  bank_label: string | null;
  statement_id: number | null;
  source_page: number | null;
  source_file: string | null;
}

const shortDate = (iso: string | null) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });
};

const txnDate = (iso: string | null) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const CATEGORY_ICON: Record<string, React.ReactNode> = {
  payroll: <Wallet size={16} />,
  business: <Building2 size={16} />,
  rental: <Home size={16} />,
  investment: <TrendingUp size={16} />,
  benefits: <Landmark size={16} />,
  other: <Receipt size={16} />,
};

/** Confidence is about cadence — how regularly the money actually arrived. */
const CONFIDENCE_TONE: Record<string, TagTone> = {
  high: 'good',
  medium: 'warn',
  low: 'neutral',
};

/**
 * The discrepancy headline.
 *
 * This is the finding, not a form field — a pay stub saying one thing while the
 * account received another is the whole reason to run this against bank data.
 * It gets the visual weight to match.
 */
function ComparisonHeadline({
  comparison,
  documentedSource,
}: {
  comparison: IncomeComparison;
  documentedSource?: string;
}) {
  const undocumented = comparison.documented_monthly == null;
  return (
    <div className="rounded-lg border border-danger-accent bg-danger-soft p-4">
      <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
        <div>
          <div className="t-eyebrow mb-1">
            {undocumented ? 'On file' : 'Documented'}
          </div>
          <div className="t-num-hero text-ink">
            {undocumented ? 'Nothing' : money(comparison.documented_monthly)}
          </div>
          <div className="mt-1 text-xs text-ink-soft">
            {undocumented ? 'No pay stub or return' : documentedSource || 'per month'}
          </div>
        </div>

        <ArrowRight size={20} className="mb-6 text-ink-faint" />

        <div>
          <div className="t-eyebrow mb-1">
            Deposits show
          </div>
          <div className="t-num-hero text-ink">
            {money(comparison.deposits_monthly)}
          </div>
          <div className="mt-1 text-xs text-ink-soft">
            {comparison.stream_count} payroll stream
            {comparison.stream_count === 1 ? '' : 's'} per month
          </div>
        </div>

        <div className="ml-auto text-right">
          <div className="t-eyebrow mb-1">Difference</div>
          <div className="t-num-hero text-danger">
            {money(comparison.delta, { signed: true })}
          </div>
          {comparison.delta_pct != null && (
            <div className="mt-1 text-xs text-danger">
              {comparison.delta_pct.toFixed(0)}% more than documented
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EvidenceTable({
  txns,
  onViewSource,
}: {
  txns: EvidenceTxn[];
  onViewSource: Props['onViewSource'];
}) {
  if (!txns.length) {
    return <p className="px-5 py-4 text-xs text-ink-faint">No itemized deposits available.</p>;
  }
  return (
    <div className="overflow-x-auto">
      {/* eslint-disable-next-line no-restricted-syntax -- evidence table with a citation-button cell; restyled in place, DataTable cannot host it */}
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="t-eyebrow w-32 px-4 py-2 text-left">Date</th>
            <th className="t-eyebrow w-40 px-4 py-2 text-left">Account</th>
            <th className="t-eyebrow px-4 py-2 text-left">Description</th>
            <th className="t-eyebrow w-20 px-4 py-2 text-left">Source</th>
            <th className="t-eyebrow w-28 px-4 py-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border-subtle">
          {txns.map((t) => {
            const hasSource = !!(t.statement_id && t.source_page);
            return (
              <tr key={t.id} className="transition-colors hover:bg-cream">
                <td className="whitespace-nowrap px-4 py-2 align-top text-xs text-ink-soft tabular-nums">
                  {txnDate(t.date)}
                </td>
                <td className="px-4 py-2 align-top text-xs text-ink-soft">
                  {t.bank_label || '—'}
                </td>
                <td className="px-4 py-2 align-top text-xs text-ink-soft">
                  {t.description || '—'}
                </td>
                <td className="px-4 py-2 align-top">
                  {hasSource ? (
                    /* eslint-disable-next-line no-restricted-syntax -- inline citation link: no ui/LinkButton primitive exists yet */
                    <button
                      onClick={() =>
                        onViewSource({
                          statementId: t.statement_id!,
                          pageNumber: t.source_page!,
                          sourceFile: t.source_file || '',
                        })
                      }
                      className="flex items-center gap-1 text-xs font-medium text-ink-soft transition-colors hover:text-ink hover:underline"
                      title={t.source_file ? `${t.source_file} (p.${t.source_page})` : undefined}
                    >
                      <Eye size={12} className="shrink-0" /> p.{t.source_page}
                    </button>
                  ) : (
                    <span className="text-xs text-ink-faint">—</span>
                  )}
                </td>
                <td className="t-num whitespace-nowrap px-4 py-2 align-top text-xs">
                  {money(t.amount, { signed: true })}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StreamCard({
  stream,
  onAccept,
  onDismiss,
  onViewSource,
  evidence,
  busy,
}: {
  stream: IncomeStream;
  onAccept: () => void;
  onDismiss: () => void;
  onViewSource: Props['onViewSource'];
  evidence: EvidenceTxn[];
  busy: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Card padding="none" className="overflow-hidden bg-surface-raised">
      <div className="flex flex-wrap items-start gap-4 px-4 py-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-clay-600">{CATEGORY_ICON[stream.category]}</span>
            <span className="truncate font-medium text-ink">{stream.label}</span>
            <Tag tone="neutral">{stream.category_label}</Tag>
            <Tag
              tone={CONFIDENCE_TONE[stream.confidence] ?? 'neutral'}
              title="How regularly the deposits arrived"
            >
              {stream.confidence} confidence
            </Tag>
          </div>
          <div className="mt-1 text-xs text-ink-soft">
            {stream.occurrences} deposits across {stream.months_observed} month
            {stream.months_observed === 1 ? '' : 's'} · {shortDate(stream.first_date)} –{' '}
            {shortDate(stream.last_date)} · averaging {money(stream.average_deposit)} each
          </div>
          <div className="mt-1 text-xs text-ink-faint">
            Would populate <span className="text-ink-soft">{stream.statute}</span>
          </div>
        </div>

        <div className="text-right">
          <div className="t-num-hero text-ink">
            {money(stream.monthly_average)}
          </div>
          <div className="text-xs text-ink-faint">per month</div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-border-subtle bg-cream px-4 py-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          icon={open ? ChevronDown : ChevronRight}
          onClick={() => setOpen((v) => !v)}
          className="text-ink-soft hover:text-ink"
        >
          {stream.occurrences} deposits
        </Button>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            icon={X}
            disabled={busy}
            onClick={onDismiss}
          >
            Not income
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            icon={Check}
            disabled={busy}
            onClick={onAccept}
          >
            Add to worksheet
          </Button>
        </div>
      </div>

      {open && (
        <div className="border-t border-border-subtle">
          <EvidenceTable txns={evidence} onViewSource={onViewSource} />
        </div>
      )}
    </Card>
  );
}

export default function IncomeDiscoveryPanel({
  displayName,
  streams,
  comparison,
  isMaterial,
  documentedSource,
  onAccept,
  onDismiss,
  onViewSource,
  evidenceFor,
  busyKey,
}: Props) {
  if (!streams.length && !isMaterial) return null;

  const total = streams.reduce((s, x) => s + x.monthly_average, 0);

  return (
    <Card padding="none" className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border-subtle px-6 py-4">
        <div className="flex items-center gap-2">
          <Search size={16} className="text-green-700" />
          <h3 className="t-card text-green-900">
            Income found in {displayName}'s deposits
          </h3>
        </div>
        {streams.length > 0 && (
          <div className="text-right">
            <div className="t-num text-sm font-semibold text-ink">
              {money(total, { whole: true })}/mo
            </div>
            <div className="text-xs text-ink-faint">across {streams.length} stream
              {streams.length === 1 ? '' : 's'}, none counted yet</div>
          </div>
        )}
      </div>

      <div className="space-y-4 p-6">
        {isMaterial && comparison && (
          <ComparisonHeadline comparison={comparison} documentedSource={documentedSource} />
        )}

        <p className="text-sm text-ink-soft">
          Recurring deposits that were not transfers between accounts. Tex. Fam. Code
          § 154.062(b) counts income <em>actually being received</em>, so these are evidence —
          but whether a deposit is income is your call, and nothing here is counted until you
          add it.
        </p>

        {streams.map((s) => (
          <StreamCard
            key={s.key}
            stream={s}
            evidence={evidenceFor(s)}
            busy={busyKey === s.key}
            onAccept={() => onAccept(s)}
            onDismiss={() => onDismiss(s)}
            onViewSource={onViewSource}
          />
        ))}

        {streams.length === 0 && (
          <p className="text-sm text-ink-faint">
            Every discovered stream has been reviewed.
          </p>
        )}
      </div>
    </Card>
  );
}
